/**
 * Created by Ning Jiang on 2021-05-03.
 */
import {NolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/NolimitAudioPlayer";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {IFadeTrackConf, IPlayTrackConf} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {NolimitHowl} from "@nolimitcity/slot-audio/bin/NolimitHowl";
import {KarenSoundConfig} from "./KarenSoundConfig";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {gsap, TweenLite} from "gsap";

export class KarenSoundManager {
    public static davidPanningReelStop: number[] = [-0.30, -0.20, -0.05, 0.05, 0.20, 0.30];
    public static _isFSSoundPlaying: boolean = false;
    public static isFSLoopPlaying: Boolean = false;
    public static isFSLoopCallBack: TweenLite

    constructor() {
    }

    private static _currentMusic: string;

    public static get currentMusic(): string {
        return this._currentMusic;
    }

    private static get soundPlayer(): NolimitAudioPlayer {
        return <NolimitAudioPlayer>SlotGame.sound;
    }

    public static playFSLoopSound(): void {
        this.fadeLoop(KarenSoundConfig.FREESPIN_WIN_LOOP, 100, this.isFSLoopPlaying ? 0 : 100);
        if (this.isFSLoopCallBack && this.isFSLoopCallBack.totalDuration()) {
            this.isFSLoopCallBack.kill();
        }
        this.isFSLoopPlaying = true;
        this.isFSLoopCallBack = gsap.delayedCall(10, () => {
            this.fadeFSLoopSound(7000)
        });
    }

    public static fadeFSGameSound(duration: number = 100, vol: number = 0) {
        this.fadeLoop(KarenSoundConfig.FREESPIN_GAME_MUSIC, vol, duration,);
    }

    public static fadeFSLoopSound(duration: number = 100) {
        this.fadeLoop(KarenSoundConfig.FREESPIN_WIN_LOOP, 0, duration);
        if (this.isFSLoopCallBack && this.isFSLoopCallBack.totalDuration()) {
            this.isFSLoopCallBack.kill();
        }
        this.isFSLoopPlaying = false;
    }

    public static switchMusic(conf: IPlayTrackConf, fadeInConf?: IFadeTrackConf, fadeOutConf?: IFadeTrackConf): void {
        if (conf.name === KarenSoundManager._currentMusic) {
            return;
        }
        Logger.logDev("sound switchMusic", fadeInConf?.name);
        if (KarenSoundManager._currentMusic != null) {
            if (fadeOutConf) {
                fadeOutConf.name = this._currentMusic;
                const fade = KarenSoundManager.soundPlayer.fadeTrack(fadeOutConf);
                const fadeName = fadeOutConf.name;
                if (fade) {
                    const fadeName = this._currentMusic;
                    gsap.to(this, {
                        duration: fadeOutConf.duration / 1000, onComplete: () => {
                            KarenSoundManager.soundPlayer.stopTrack(fadeName);
                        }
                    });
                } else {
                    KarenSoundManager.soundPlayer.stopTrack(fadeName);
                }
            } else {
                KarenSoundManager.soundPlayer.stopTrack(KarenSoundManager._currentMusic);
            }
        }

        KarenSoundManager.soundPlayer.playTrack(conf);
        if (fadeInConf) {
            fadeInConf.name = conf.name;
            KarenSoundManager.soundPlayer.fadeTrack(fadeInConf);
        }
        KarenSoundManager._currentMusic = conf.name;
    }

    public static fadeSpecificMusic(name: string, vol: number, duration: number): void {
        Logger.logDev(name, " sound fadeMusic", duration);
        if (this._isFSSoundPlaying && vol == 1) {
            return;
        }
        KarenSoundManager.soundPlayer.fadeTrack({
            name: name,
            to: vol,
            duration: duration
        });
        KarenSoundManager._isFSSoundPlaying = vol === 1;
    }

    public static fadeAndStopSound(fadeOutConf: IFadeTrackConf): void {
        Logger.logDev("sound fadeOut:", fadeOutConf.name);
        const fade: NolimitHowl | undefined = KarenSoundManager.soundPlayer.fadeTrack(fadeOutConf);
        if (fade) {
            gsap.to(this, {
                duration: fadeOutConf.duration / 1000,
                onComplete: () => {KarenSoundManager.soundPlayer.stopTrack(fadeOutConf.name)}
            });
        } else {
            KarenSoundManager.soundPlayer.stopTrack(fadeOutConf.name);
        }
    }

    // Use this to handle the music, eg, fade out/in music in big win, and you don't need to care what music is playing
    public static fadeMusic(vol: number, duration: number, name?: string): void {
        Logger.logDev("vol ", vol, " sound fade current Music", name ? name : KarenSoundManager._currentMusic);
        KarenSoundManager.soundPlayer.fadeTrack({
            name: name ? name : KarenSoundManager._currentMusic,
            to: vol,
            duration: duration
        });
    }

    public static getBackgroundMusicNameByMode(mode: GameMode | string): string {
        Logger.logDev("sound playing for mode:", mode);
        switch (mode) {
            default:
                return KarenSoundConfig.MAIN_GAME_MUSIC;
        }
    }

    public static playSoundEffect(name: string, repeat?: number, volume?: number): NolimitHowl | undefined {
        if (!name) debugger;
        Logger.logDev("sound playSoundEffect:", name, repeat, volume);
        return this.soundPlayer.playTrack({
            name: name,
            repeat: repeat,
            volume: volume
        });
    }

    public static playTrack(name: string, vol?: number): void {
        Logger.logDev("sound playtrack: ", name, " vol ", vol);
        this.soundPlayer.playTrack({name: name, volume: vol});
    }

    public static stopSoundEffect(name: string): void {
        Logger.logDev("sound stopSoundEffect:", name);
        this.soundPlayer.stopTrack(name);
    }

    public static playDoNotLoopSound(name: string): void {
        Logger.logDev("sound playtrack: ", name);
        this.soundPlayer.playTrack({name: name})?.setLoop(false);
    }

    public static playLoopSound(name: string): void {
        this.soundPlayer.playTrack({name: name})?.setLoop(true);
    }

    public static getProgress(name: string) {
        return KarenSoundManager.soundPlayer.fetchSoundPlayer(name)?.trackDuration();
    }

    public static isEmpty(name: string) {
        return KarenSoundManager.soundPlayer.fetchSoundPlayer(name)?.isEmpty;
    }

    public static fadeLoop(name: string, vol: number, duration: number = 100) {
        let player = KarenSoundManager.soundPlayer.fetchSoundPlayer(name);
        if (name && player?.isPlaying) {
            player.fade(player.currentVolume(), vol, duration);
        }
    }
}
